// Indicators (DESIGN_SPEC §12.9, §10.11). behavior-report.json `iocs` is authoritative after the run; while it
// runs, the store's preview stands in ("so far"). Values are grouped by kind into a Network side (domain · ipv4
// · ipv6 · url · …) and a Host side (registry key · mutex · file path · hashes · …); OS background (noise.ts) and
// the guest's own subnet are kept apart per table; a %TEMP%\tmp*.tmp burst, and one writer's MASS_FILES+ same-way
// paths, fold into one expandable row; values only the static report produced carry "static", and static strings
// that read like code namespaces say so.
//   groupIocs(input) → IocGroup[]            network tables first, each with rows + background
//   iocSet(input)    → { network, host, shown, background, staticOnly, source }
//   iocsOf(ctx)      → IocSet                the report's (behavior-report.json, else the live preview), memoized
//   seenWords(sources) "dns · http" · iocValues(tables) · iocsCsv(tables) · iocsJson(tables) · backgroundWords(rows)
import { envPath } from "@/lib/format";
import { backgroundIoc, classifyNoise } from "@/lib/derive/noise";
import type { IocGroup, IocRow, IocSide, NoiseIndex, NoiseService, ReportCtx } from "@/lib/derive/types";
import type { BehaviorReport, Event, IocEntry, IocKind } from "@/types";
import type { IocPreview } from "@/lib/eventStore";
import { MASS_FILES, globOf, noiseOf, num, procResolver, str } from "./fold";

type GroupIocsInput = {
  /** behavior-report.json: authoritative after the end. */
  report?: BehaviorReport;
  /** The store's live preview (used when there is no report). */
  preview?: readonly IocPreview[];
  events?: readonly Event[];
  noise?: NoiseIndex;
};

export type IocItem = Omit<IocRow, "children"> & {
  /** raw event kinds / static sources, as reported */
  sources: string[];
  /** a static string that reads like a code namespace rather than a host */
  note?: string;
  children?: IocItem[];
};
export type IocTable = Omit<IocGroup, "rows" | "background"> & { rows: IocItem[]; background: IocItem[] };
export type IocSet = {
  network: IocTable[];
  host: IocTable[];
  /** indicators shown (not background; folded rows count every value) */
  shown: number;
  background: number;
  /** shown values that only the static report produced */
  staticOnly: number;
  source: "report" | "preview" | "none";
};

const NETWORK_ORDER: readonly string[] = ["domain", "ipv4", "ipv6", "url", "email", "network.onion", "cloud.metadata-host", "exfil.discord-webhook", "exfil.telegram-bot", "exfil.pastebin"];
const HOST_ORDER: readonly string[] = ["registry_key", "mutex", "file_path", "sha256", "md5", "sha1", "imphash", "pdb_path", "cert_thumbprint", "wallet.btc", "wallet.eth", "wallet.xmr"];

const LABELS: Record<string, string> = {
  domain: "domain",
  ipv4: "ipv4",
  ipv6: "ipv6",
  url: "url",
  email: "email",
  "network.onion": "onion address",
  "cloud.metadata-host": "cloud metadata host",
  "exfil.discord-webhook": "discord webhook",
  "exfil.telegram-bot": "telegram bot",
  "exfil.pastebin": "pastebin",
  registry_key: "registry key",
  mutex: "mutex",
  file_path: "file path",
  sha256: "sha-256",
  md5: "md5",
  sha1: "sha-1",
  imphash: "imphash",
  pdb_path: "pdb path",
  cert_thumbprint: "cert thumbprint",
  "wallet.btc": "bitcoin wallet",
  "wallet.eth": "ethereum wallet",
  "wallet.xmr": "monero wallet",
};

const iocLabel = (kind: string): string => LABELS[kind] ?? kind.replace(/[._-]+/g, " ");
const sideOf = (kind: string): IocSide => (NETWORK_ORDER.includes(kind) || kind.startsWith("exfil.") || kind.startsWith("network.") ? "network" : "host");

const SEEN: Record<string, string> = {
  "net.dns": "dns",
  "net.http": "http",
  "net.conn": "conn",
  "net.flow": "flow",
  "file.create": "create",
  "file.write": "write",
  "file.delete": "delete",
  "file.rename": "rename",
  "file.drop": "drop",
  "registry.set": "set",
  "registry.delete": "delete",
  "mutex.create": "mutex",
  "image.load": "load",
  "proc.start": "start",
  "yara.match": "yara",
  "config.extracted": "config",
  "dump.taken": "dump",
};

const isStatic = (s: string) => s === "static" || s.startsWith("static.");

/** "net.dns, net.http" → "dns · http"; every static source reads "static". */
export function seenWords(sources: readonly string[]): string[] {
  const out: string[] = [];
  for (const s of sources) {
    const w = isStatic(s) ? "static" : (SEEN[s] ?? s);
    if (!out.includes(w)) out.push(w);
  }
  return out;
}

const KNOWN_TLDS = new Set(
  "com net org io info biz co us uk de fr ru cn jp br in it nl es eu ca au me tv cc ws xyz top site online club app dev live pro su tk ml ga cf gq pw onion example test local invalid localhost ir kp ua by kz".split(" "),
);

/** A static string like "System.Drawing" or "Pulsar.Common.Messages.UAC": a .NET namespace, not a host. */
function namespaceNote(kind: string, value: string, sources: readonly string[]): string | undefined {
  if (kind !== "domain" || !sources.length || !sources.every(isStatic)) return undefined;
  const labels = value.split(".");
  const tld = labels[labels.length - 1] ?? "";
  if (labels.some((l) => /^[A-Z]/.test(l)) || !KNOWN_TLDS.has(tld.toLowerCase())) return "reads like a code namespace, not a host";
  return "a string in the file; never resolved";
}

const TEMP_TMP_RE = /^%TEMP%\\(?:[^\\]*\\)*tmp[^\\]*\.tmp$/i;

type Entry = { kind: IocKind; value: string; hits: number; sources: string[]; firstSeq?: number };

function entries(input: GroupIocsInput): { list: Entry[]; source: IocSet["source"] } {
  if (input.report) {
    const list: Entry[] = [];
    for (const [kind, items] of Object.entries(input.report.iocs ?? {})) {
      for (const i of (items ?? []) as IocEntry[]) {
        const value = str(i?.value).trim();
        if (!value) continue;
        list.push({ kind: (str(i.kind) || kind) as IocKind, value, hits: num(i.hits) ?? 1, sources: Array.isArray(i.sources) ? i.sources.map(str).filter(Boolean) : [] });
      }
    }
    return { list, source: "report" };
  }
  if (input.preview?.length) {
    return { list: input.preview.map((p) => ({ kind: p.kind, value: p.value, hits: p.hits, sources: [...p.sources], firstSeq: p.firstSeq })), source: "preview" };
  }
  return { list: [], source: "none" };
}

/** Writers of each %TEMP% path, for the fold label ("9 paths from winsvc.exe"). */
function writersOf(events: readonly Event[] | undefined): Map<string, string> {
  const out = new Map<string, string>();
  if (!events?.length) return out;
  const name = procResolver(events);
  for (const e of events) {
    if (!e.kind.startsWith("file.")) continue;
    const p = str(e.kind === "file.rename" ? e.fields?.to : e.fields?.path).toLowerCase();
    const pid = num(e.fields?.pid);
    if (p && pid !== undefined && !out.has(p)) out.set(p, name(pid, e.seq) ?? `pid ${pid}`);
  }
  return out;
}

const byHits = (a: IocItem, b: IocItem) => Number(a.static) - Number(b.static) || b.hits - a.hits || a.value.localeCompare(b.value);

function foldTemp(rows: IocItem[], writers: Map<string, string>): IocItem[] {
  const temp = rows.filter((r) => TEMP_TMP_RE.test(envPath(r.value)));
  if (temp.length < 3) return rows;
  const procs = [...new Set(temp.map((r) => writers.get(r.value.toLowerCase())).filter((w): w is string => !!w))];
  const from = procs.length === 1 ? ` from ${procs[0]}` : procs.length > 1 ? ` from ${procs.length} processes` : "";
  const fold: IocItem = {
    kind: "file_path",
    value: "%TEMP%\\tmp*.tmp",
    hits: temp.reduce((s, r) => s + r.hits, 0),
    seenIn: [...new Set(temp.flatMap((r) => r.seenIn))],
    sources: [...new Set(temp.flatMap((r) => r.sources))],
    static: false,
    background: false,
    children: temp.sort(byHits),
    foldLabel: `${temp.length} paths${from}`,
  };
  const rest = rows.filter((r) => !temp.includes(r));
  return [...rest, fold].sort(byHits);
}

/** One writer touching MASS_FILES or more paths the same way (ransomware, collectors) folds into one glob row. */
function foldMass(rows: IocItem[], writers: Map<string, string>): IocItem[] {
  const groups = new Map<string, IocItem[]>();
  for (const r of rows) {
    if (r.children || r.static) continue;
    const k = `${writers.get(r.value.toLowerCase()) ?? ""}\0${r.seenIn.join(",")}`;
    (groups.get(k) ?? groups.set(k, []).get(k)!).push(r);
  }
  const folded = new Set<IocItem>();
  const out: IocItem[] = [];
  for (const [k, list] of groups) {
    if (list.length < MASS_FILES) continue;
    const writer = k.split("\0")[0];
    const dirs = new Set(list.map((r) => envPath(r.value).toLowerCase().replace(/\\[^\\]*$/, ""))).size;
    list.forEach((r) => folded.add(r));
    out.push({
      kind: "file_path",
      value: globOf(list.map((r) => r.value)),
      hits: list.reduce((s, r) => s + r.hits, 0),
      seenIn: list[0].seenIn,
      sources: [...new Set(list.flatMap((r) => r.sources))],
      static: false,
      background: false,
      children: [...list].sort(byHits),
      foldLabel: `${list.length} paths${dirs > 1 ? ` in ${dirs} folders` : ""}${writer ? ` from ${writer}` : ""}`,
    });
  }
  return out.length ? [...rows.filter((r) => !folded.has(r)), ...out].sort(byHits) : rows;
}

export function iocSet(input: GroupIocsInput): IocSet {
  const { list, source } = entries(input);
  const noise = input.noise ?? (input.events ? classifyNoise(input.events) : undefined);
  const writers = writersOf(input.events);
  const byKind = new Map<string, IocItem[]>();
  const seen = new Set<string>();
  for (const e of list) {
    const k = `${e.kind}\0${e.value}`;
    if (seen.has(k)) continue;
    seen.add(k);
    const svc: NoiseService | null = backgroundIoc(e.kind, e.value, noise);
    const item: IocItem = {
      kind: e.kind,
      value: e.value,
      hits: e.hits,
      seenIn: seenWords(e.sources),
      sources: e.sources,
      static: e.sources.length > 0 && e.sources.every(isStatic),
      background: !!svc,
      service: svc ?? undefined,
      firstSeq: e.firstSeq,
      note: namespaceNote(e.kind, e.value, e.sources),
    };
    (byKind.get(e.kind) ?? byKind.set(e.kind, []).get(e.kind)!).push(item);
  }

  const order = (side: IocSide) => {
    const known = side === "network" ? NETWORK_ORDER : HOST_ORDER;
    const kinds = [...byKind.keys()].filter((k) => sideOf(k) === side);
    return kinds.sort((a, b) => (known.indexOf(a) < 0 ? 99 : known.indexOf(a)) - (known.indexOf(b) < 0 ? 99 : known.indexOf(b)) || a.localeCompare(b));
  };
  const table = (side: IocSide, kind: string): IocTable => {
    const all = byKind.get(kind) ?? [];
    let rows = all.filter((r) => !r.background).sort(byHits);
    if (kind === "file_path") rows = foldMass(foldTemp(rows, writers), writers);
    return { side, kind, label: iocLabel(kind), rows, background: all.filter((r) => r.background).sort(byHits) };
  };
  const network = order("network").map((k) => table("network", k));
  const host = order("host").map((k) => table("host", k));
  const flat = (rows: IocItem[]) => rows.flatMap((r) => r.children ?? [r]);
  const tables = [...network, ...host];
  const shownItems = tables.flatMap((t) => flat(t.rows));
  return {
    network,
    host,
    shown: shownItems.length,
    background: tables.reduce((s, t) => s + t.background.length, 0),
    staticOnly: shownItems.filter((r) => r.static).length,
    source,
  };
}

export function groupIocs(input: GroupIocsInput): IocGroup[] {
  const s = iocSet(input);
  return [...s.network, ...s.host];
}

// ---- export -------------------------------------------------------------------------------------------

type AnyTable = Pick<IocTable, "rows" | "background">;
const flatRows = (rows: readonly IocItem[]) => rows.flatMap((r) => r.children ?? [r]);

/** The values of the shown rows (folded rows expand), optionally with background. */
export function iocValues(tables: readonly AnyTable[], withBackground = false): IocItem[] {
  return tables.flatMap((t) => [...flatRows(t.rows), ...(withBackground ? t.background : [])]);
}

/** The report's shape: { iocs: { kind: [{ kind, value, sources, hits }] } }. */
export function iocsJson(tables: readonly AnyTable[], withBackground = true): string {
  const out: Record<string, IocEntry[]> = {};
  for (const r of iocValues(tables, withBackground)) (out[r.kind] ??= []).push({ kind: r.kind, value: r.value, sources: r.sources, hits: r.hits });
  return `${JSON.stringify({ iocs: out }, null, 2)}\n`;
}

/** A spreadsheet cell: quoted when needed, and never read as a formula. */
function cell(v: string): string {
  const safe = /^[=+\-@\t\r]/.test(v) ? `'${v}` : v;
  return /[",\n\r]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
}

/** kind,value,hits,seen_in,static,background */
export function iocsCsv(tables: readonly AnyTable[], withBackground = true): string {
  const lines = ["kind,value,hits,seen_in,static,background"];
  for (const r of iocValues(tables, withBackground)) lines.push([r.kind, r.value, String(r.hits), r.sources.join(";"), String(r.static), String(r.background)].map(cell).join(","));
  return `${lines.join("\n")}\n`;
}

/** "%APPDATA%\WinSvc\winsvc.exe" for paths; other kinds as they are. */
export function iocDisplay(kind: string, value: string): string {
  return kind === "file_path" || kind === "pdb_path" ? envPath(value) : value;
}

/** Middle-truncation tail per kind: the part an analyst reads first stays visible. */
export function iocTail(kind: string, value: string): number | "file" {
  if (kind === "file_path" || kind === "pdb_path" || kind === "registry_key") return "file";
  if (kind === "url") return Math.min(value.length, 24);
  if (kind === "sha256" || kind === "sha1" || kind === "md5" || kind === "imphash" || kind === "cert_thumbprint") return 8;
  if (kind === "mutex") return Math.min(value.length, 14);
  return Math.min(value.length, 18);
}

/** Plain words for the services behind a table's background rows. */
export function backgroundWords(rows: readonly IocItem[]): string {
  const words: string[] = [];
  const add = (w: string) => {
    if (!words.includes(w)) words.push(w);
  };
  for (const r of rows) {
    switch (r.service) {
      case "NCSI":
        add("Windows connectivity");
        break;
      case "Windows Update":
        add("update");
        break;
      case "Microsoft":
      case "Bing":
        add("Microsoft");
        break;
      case "Certificates":
        add("certificates");
        break;
      case "Local network":
        add("guest subnet");
        break;
      case undefined:
        break;
      default:
        add("discovery and multicast");
    }
  }
  return words.join(", ");
}

// ---- report context (memoized per job) ------------------------------------------------------------------

const SETS = new Map<string, { version: number; report?: BehaviorReport; set: IocSet }>();

/** behavior-report.json once the run is over, the store's preview while it runs. */
export function iocsOf(ctx: ReportCtx): IocSet {
  const report = ctx.mode === "live" ? undefined : ctx.behaviorReport;
  const version = ctx.stream.snap.version;
  const m = SETS.get(ctx.job.id);
  if (m && m.version === version && m.report === report) return m.set;
  const set = iocSet({ report, preview: report ? undefined : [...ctx.stream.snap.iocs.values()], events: ctx.stream.snap.events, noise: noiseOf(ctx) });
  SETS.delete(ctx.job.id);
  SETS.set(ctx.job.id, { version, report, set });
  if (SETS.size > 8) SETS.delete(SETS.keys().next().value!);
  return set;
}
